import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAccessToken, useTenantData } from "@/hooks";
import { PrivatesAppointmentsApi } from "@/openapi/generated";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi } from "@/lib/utils";
import {
  parseAvailableRoomsResponse,
  type AvailableRoomOption,
} from "../utils/parse-available-rooms-response";

interface UseBookingAvailableRoomsGetParams {
  bookingId: number | null;
  enabled?: boolean;
}

export function useBookingAvailableRoomsGet({
  bookingId,
  enabled = true,
}: UseBookingAvailableRoomsGetParams) {
  const accessToken = useAccessToken();
  const { tenantId } = useTenantData();
  const id = bookingId ?? 0;

  return useQuery({
    queryKey: [QueryKeys.AppointmentBookings, "available-rooms", id],
    queryFn: async (): Promise<AvailableRoomOption[]> => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }
      const service = createAuthenticatedApi(PrivatesAppointmentsApi, accessToken);
      const response = await service.apiAppointmentsBookingIdAvailableRoomsGet({
        id,
        xTenantID: tenantId,
      });
      return parseAvailableRoomsResponse(response);
    },
    enabled: enabled && !!accessToken && id > 0,
  });
}

interface UseBookClassroomParams {
  bookingId: number;
  classRoomId: number;
}

export function useBookClassroom() {
  const accessToken = useAccessToken();
  const { tenantId } = useTenantData();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ bookingId, classRoomId }: UseBookClassroomParams) => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }
      const service = createAuthenticatedApi(PrivatesAppointmentsApi, accessToken);
      await service.apiAppointmentsBookingIdBookClassroomPut({
        id: bookingId,
        bookClassRoomRequest: { classRoomId },
        xTenantID: tenantId,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.AppointmentBookings],
      });
    },
  });
}
